import { Drawer } from '@dynamika-shared/ui/Drawer/Drawer';
import { DrawerProps } from 'antd';
import { FC, useEffect, useState } from 'react';
import { Option } from '@utils/generateOptions';
import { Button } from '@dynamika-shared/ui/Button/Button';
import { CloseOutlined } from '@ant-design/icons';
import { useParams } from 'react-router-dom';
import { ModalityLabel } from '@dynamika-shared/types/imagingLabels';
import { SelectedItems } from '@components/SelectedItems/SelectedItems';
import { SelectLabels } from '@components/SelectLabels/SelectLabels';
import { UnsavedChangesModal } from '@components/modals/UnsavedChangesModal/UnsavedChangesModal';
import { useImagingLabelsSlice } from '@restore/imagingLabels/imagingLabels.slice';
import { useClinicalTrialSlice } from '@restore/clinicalTrial/clinicalTrial.slice';

type LabelMapperProps = DrawerProps & {
  close?: () => void;
  ctLabels: ModalityLabel[];
  open: boolean;
};

export const LabelMapper: FC<LabelMapperProps> = ({
  close,
  ctLabels,
  open,
}) => {
  const [imagingLabels, fetchItems, loading, assignLabels] =
    useImagingLabelsSlice((state, actions) => [
      state.data,
      actions.fetchImagingLabels,
      state.loading,
      actions.assignLabelsRequest,
    ]);

  const clinicalTrial = useClinicalTrialSlice(state => state.data);
  const [selectedItems, setSelectedItems] = useState<Option[]>([]);
  const [searchValue, setSearchValue] = useState<string>('');
  const [askQuit, setAskQuit] = useState<boolean>(false);

  const { id: clinicalTrialId } = useParams<{
    id: string;
  }>();

  useEffect(() => {
    setSelectedItems([]);
    setSearchValue('');
  }, [clinicalTrial]);

  const handleTagClose = (removedItem: Option) => {
    const updatedItems = selectedItems.filter(item => item !== removedItem);
    setSelectedItems(updatedItems);
  };

  const handleAssignLabels = async () => {
    if (clinicalTrialId) {
      const ids = selectedItems.map(item => item.value);
      assignLabels({ clinicalTrialId, data: ids });
    }
  };

  const onCloseDrawer = () => {
    if (selectedItems.length === 0) {
      close && close();
    } else {
      setAskQuit(true);
    }
  };
  useEffect(() => {
    !searchValue && fetchItems();
  }, [searchValue]);
  return (
    <Drawer
      classNames={{
        header:
          '!px-0 [&_button]:absolute [&_button]:top-[1.3rem] [&_button]:right-[0.4rem]',
        body: 'flex flex-col !px-0',
        content: 'rounded-lg',
      }}
      title={'Add Label'}
      open={open}
      width={464}
      placement='right'
      onClose={onCloseDrawer}
      closeIcon={<CloseOutlined />}
    >
      <div className='px-1'>
        <SelectLabels
          searchValue={searchValue}
          selectedItems={selectedItems}
          setSelectedItems={setSelectedItems}
          onSearch={setSearchValue}
          ctLabels={ctLabels}
          imagingLabels={imagingLabels}
          fetchItems={name => {
            fetchItems(name);
          }}
        />
        <SelectedItems
          selectedItems={selectedItems}
          onClose={handleTagClose}
          title={'Selected labels'}
        />
      </div>
      <footer className='mt-auto'>
        <hr className='mb-3' />
        <div className='flex justify-between'>
          <Button
            className='w-[17rem]'
            name='cancel-button'
            onClick={onCloseDrawer}
          >
            Cancel
          </Button>
          <Button
            className='w-[17rem]'
            type='primary'
            name='add-label-button'
            disabled={selectedItems.length === 0 || loading}
            onClick={handleAssignLabels}
          >
            Add
          </Button>
        </div>
      </footer>
      <UnsavedChangesModal
        open={askQuit}
        onConfirm={() => {
          setSelectedItems([]);
          setAskQuit(false);
          close && close();
        }}
        onCancel={() => setAskQuit(false)}
      />
    </Drawer>
  );
};
